import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, Clock, FileDown } from "lucide-react";
import { api, API, money, fmtDate } from "../lib/api";
import { useAuth } from "../lib/store";
import { Empty } from "../components/Bits";
import { useSeo } from "../components/Seo";

export default function OrderDetail() {
  const { id } = useParams();
  useSeo({ title: "Objednávka", noindex: true });
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    if (!user) return;
    const load = () => api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => setErr(true));
    if (user) api.get(`/payments/comgate/status/${id}`).catch(() => {}).finally(load);
  }, [id, user]);
  if (user === false) return <Empty>Pro zobrazení objednávky se <Link to={`/prihlaseni?next=/objednavka/${id}`} className="underline">přihlaste</Link>.</Empty>;
  if (err) return <Empty>Objednávku jsme nenašli.</Empty>;
  if (!o) return <div className="h-[60vh]" />;
  const paid = o.status === "paid";
  return (
    <div className="max-w-4xl mx-auto px-6 py-14" data-testid="order-detail">
      <div className="text-center">
        {paid ? <CheckCircle2 className="w-16 h-16 mx-auto text-[var(--teal)]" /> : <Clock className="w-16 h-16 mx-auto text-[var(--pink)]" />}
        <h1 className="text-4xl font-semibold mt-4">{paid ? "Zaplaceno, děkujeme!" : o.status === "cancelled" ? "Objednávka zrušena" : "Děkujeme za objednávku!"}</h1>
        <p className="text-[var(--muted)] mt-2">Objednávka č. <b data-testid="order-number">{o.number}</b> · {fmtDate(o.created_at)}</p>
      </div>
      {o.status === "pending" && o.payment_method === "bank" && (
        <div className="mt-10 grid md:grid-cols-[auto_1fr] gap-8 items-center rounded-[32px] bg-[var(--blush)] p-8" data-testid="order-payment-box">
          {o.spayd ? <div className="bg-white p-4 rounded-3xl"><QRCodeSVG value={o.spayd} size={190} data-testid="order-qr-code" /></div> : <p className="text-sm">QR kód se zobrazí po doplnění čísla účtu v administraci.</p>}
          <div className="space-y-2">
            <p className="font-display text-xl">Zaplaťte převodem nebo naskenujte QR kód</p>
            {o.bank.account && <p>Číslo účtu: <b>{o.bank.account}</b></p>}
            {o.bank.iban && <p>IBAN: <b>{o.bank.iban}</b></p>}
            <p>Variabilní symbol: <b>{o.number}</b></p>
            <p>Částka: <b>{money(o.total)}</b></p>
            <p className="text-sm text-[var(--muted)]">{o.shipping ? "Po připsání platby vám pošleme fakturu a zásilku odešleme." : "Po připsání platby vám pošleme fakturu a materiály se odemknou ve vašem účtu."}</p>
          </div>
        </div>
      )}
      {o.shipping && (
        <div className="mt-8 rounded-[28px] border-2 border-[var(--teal-soft)] p-6 text-sm" data-testid="order-detail-shipping">
          <p className="font-display text-lg">Doprava: {o.shipping.carrier_name} · {o.shipping.mode_name}</p>
          <p>{[o.shipping.name, o.shipping.street, o.shipping.zip, o.shipping.city].filter(Boolean).join(", ")}</p>
          <p className="text-[var(--muted)] mt-1">{o.shipping.mode === "address" ? "Zásilku doručíme na tuto adresu." : `Zásilku pošleme na nejbližší dostupné ${o.shipping.mode === "zbox" ? "Z-BOX" : "výdejní místo"} podle této adresy.`}</p>
          {o.tracking_number && <p className="mt-2" data-testid="order-detail-tracking">Číslo zásilky: <b>{o.tracking_number}</b> · <a href={o.tracking_url} target="_blank" rel="noreferrer" className="underline font-bold text-[var(--pink-dark,#c46fbd)]">Sledovat zásilku</a></p>}
        </div>
      )}
      <div className="mt-8 rounded-[28px] border border-[#f6e3ec] p-6 space-y-2">
        {o.items.map((i, k) => <div key={k} className="flex justify-between"><span>{i.name}{i.qty > 1 ? ` × ${i.qty}` : ""}</span><span>{money(i.price * (i.qty || 1))}</span></div>)}
        {o.discount_amount > 0 && <div className="flex justify-between text-[var(--teal)]"><span>Sleva {o.discount_code}</span><span>−{money(o.discount_amount)}</span></div>}
        {o.voucher_amount > 0 && <div className="flex justify-between text-[var(--teal)]"><span>Poukaz {o.voucher_code}</span><span>−{money(o.voucher_amount)}</span></div>}
        <div className="flex justify-between text-xl font-extrabold border-t border-dashed border-[#f3dbe6] pt-3"><span>Celkem</span><span>{money(o.total)}</span></div>
      </div>
      <div className="flex flex-wrap gap-3 justify-center mt-8">
        <Link to="/ucet" className="btn-pink" data-testid="order-account-btn">Moje materiály</Link>
        {o.invoice_number && <a href={`${API}/orders/${o.id}/invoice.pdf`} target="_blank" rel="noreferrer" className="btn-ghost" data-testid="order-invoice-btn"><FileDown className="w-4 h-4" /> Faktura {o.invoice_number}</a>}
      </div>
    </div>
  );
}
